// Inspection photo test page — accessible from reservation detail
// Uses InspectionCamera + InspectionChecklist components for real-world testing

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import InspectionCamera from '../../components/inspection/InspectionCamera';
import InspectionChecklist from '../../components/inspection/InspectionChecklist';

export default function InspectionTestPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'pickup' | 'return'>('pickup');
  const [photos, setPhotos] = useState<Record<string, { id: string; url: string }>>({});
  const [activeItemKey, setActiveItemKey] = useState<string | null>(null);
  const [inspectionComplete, setInspectionComplete] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('carp2p_token');
    fetch(`/api/reservations/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setReservation(data);
        // Load existing inspection photos into the checklist
        const photoMap: Record<string, { id: string; url: string }> = {};
        if (data.inspection_photos) {
          data.inspection_photos.forEach((p: any) => {
            // Map photo_category to checklist key
            photoMap[p.photo_category || 'general'] = { id: p.id, url: p.photo_url };
          });
        }
        setPhotos(photoMap);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('ko-KR') : '-';

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button onClick={() => navigate(-1)} className="text-sm text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
        뒤로
      </button>

      <h1 className="text-2xl font-bold text-slate-900 mb-2">인수인계 사진 촬영</h1>
      {reservation && (
        <p className="text-sm text-gray-500 mb-6">
          {reservation.vehicles?.[0]?.make} {reservation.vehicles?.[0]?.model} · {formatDate(reservation.pickup_date)} ~ {formatDate(reservation.return_date)}
        </p>
      )}

      {/* Mode toggle */}
      <div className="flex gap-2 mb-6">
        {(['pickup', 'return'] as const).map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              mode === m
                ? 'bg-slate-900 text-white shadow-md'
                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {m === 'pickup' ? '🚗 인수 시 촬영' : '🔙 반납 시 촬영'}
          </button>
        ))}
      </div>

      {/* Checklist */}
      <div className="mb-6">
        <InspectionChecklist
          mode={mode}
          photos={photos}
          onCapture={(itemKey) => setActiveItemKey(itemKey)}
        />
      </div>

      {/* Camera — shown when a checklist item is selected */}
      {activeItemKey && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-slate-900">
              📸 {activeItemKey} 촬영
            </h3>
            <button onClick={() => setActiveItemKey(null)} className="text-xs text-gray-400 hover:text-gray-600 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
              닫기
            </button>
          </div>
          <InspectionCamera
            stepType={mode}
            reservationId={id || ''}
            onUpload={(photo) => {
              setPhotos(prev => ({
                ...prev,
                [activeItemKey]: { id: photo.id, url: photo.photoUrl || photo.previewUrl },
              }));
            }}
          />
        </div>
      )}

      {/* Completion button */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        {inspectionComplete ? (
          <div className="text-center py-3">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="font-semibold text-emerald-700">{mode === 'pickup' ? '인수' : '반납'} 검수 완료</p>
            <p className="text-xs text-gray-400 mt-1">
              {Object.keys(photos).length}장의 사진이 업로드되었습니다
            </p>
            <button onClick={() => {
              setInspectionComplete(false);
              setMode(mode === 'pickup' ? 'return' : 'pickup');
            }} className="mt-3 text-xs text-brand-600 hover:underline transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
              {mode === 'pickup' ? '반납 검수 진행하기 →' : '인수 검수 다시 보기 →'}
            </button>
          </div>
        ) : (
          <button
            onClick={async () => {
              if (Object.keys(photos).length === 0) {
                if (!confirm('사진이 한 장도 없습니다. 그래도 완료하시겠습니까?')) return;
              }
              setCompleting(true);
              try {
                const token = localStorage.getItem('carp2p_token');
                const event = mode === 'pickup' ? 'pickup' : 'return';
                await fetch(`/api/reservations/${id}/transition`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ event, notes: `${mode === 'pickup' ? '인수' : '반납'} 검수 완료 (${Object.keys(photos).length}장)` }),
                });
                setInspectionComplete(true);
              } catch (err: any) {
                // Transition might not be valid - still mark as complete locally
                setInspectionComplete(true);
              }
              setCompleting(false);
            }}
            disabled={completing}
            className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {completing ? '처리 중...' : `${mode === 'pickup' ? '인수' : '반납'} 검수 완료 (${Object.keys(photos).length}장)`}
          </button>
        )}
      </div>
    </div>
  );
}
